import '@/styles/pages/StoreMgmt/history/StoreHistoryPage.scss';
import { useEffect, useReducer, KeyboardEvent, useMemo, useRef, Reducer, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import {
    GridColDef,
    GridActionsCellItem,
    GridPaginationModel,
    GridSortModel,
    GridColumnVisibilityModel,
    GridRowParams,
    type GridApi,
} from '@mui/x-data-grid';
import { Download, WorkHistory, Summarize } from '@mui/icons-material';
import { handleFetchHistoryListAPI } from '@/api/storeManagement/history';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTitle from '@/components/ui/BaseTitle';
import StoreFilterBlock from '@/components/store/StoreFilterBlock';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import BaseTextField from '@/components/form/BaseTextField';
import BaseDataTable from '@/components/ui/BaseDataTable';
import NoStoreSelectionBlock from '@/components/store/NoStoreSelectionBlock';
import {
    StoreHistoryPageState,
    StoreHistoryPageAction,
    HistoryRecord,
    SummaryInfo,
    HistoryType,
    HISTORY_TYPE,
    HistorySearchQuery,
} from '@/types/pages/storeManagement/history';
import BaseSelect from '@/components/form/BaseSelect';
import { useAppContext } from '@/provider/contexts';
import { Box, IconButton, FormControl, Typography } from '@mui/material';
import SummaryDialog from './SummaryDialog';
import { useTranslation } from 'react-i18next';
import BaseTableColumn from '@/components/ui/BaseTableColumn';
import { useOutletContext } from 'react-router-dom';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';

type ActionOptions = {
    label: string;
    value: string;
};

const MIN_DATE_TIME = dayjs('1900-01-01');

const hasReversedTimeRange = ({ start_time, end_time }: HistorySearchQuery) =>
    Boolean(start_time?.isValid() && end_time?.isValid() && end_time.isBefore(start_time));

const defaultState: StoreHistoryPageState = {
    tableLoading: true,
    searchQuery: {
        start_time: null,
        end_time: null,
        keyword: '',
        action_type: HISTORY_TYPE.PP,
    },
    historyList: [],
    rowCount: 0,
    sortModel: { field: null, sort: null },
    paginationModel: {
        page: 0,
        pageSize: 5,
    },
    openSummaryDialog: false,
    summaryInfo: {} as SummaryInfo,
    jobId: null,
};

const reducer: Reducer<StoreHistoryPageState, StoreHistoryPageAction> = (state, action) => {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_SEARCH_QUERY':
            return { ...state, searchQuery: action.payload };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_HISTORY_TABLE':
            return {
                ...state,
                tableLoading: false,
                historyList: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_SORT_MODEL':
            return { ...state, sortModel: action.payload };
        case 'SET_SUMMARY_DIALOG_OPEN':
            return { ...state, openSummaryDialog: action.payload };
        case 'SET_SUMMARY_INFO':
            return { ...state, summaryInfo: action.payload };
        case 'SET_JOB_ID':
            return { ...state, jobId: action.payload };
        case 'CLEAR_FILTER': {
            const { start_time, end_time } = action.payload;
            return {
                ...state,
                tableLoading: true,
                historyList: [],
                rowCount: 0,
                sortModel: { field: null, sort: null },
                paginationModel: { page: 0, pageSize: 5 },
                searchQuery: {
                    start_time,
                    end_time,
                    keyword: '',
                    action_type: HISTORY_TYPE.PP,
                },
            };
        }
        default:
            return state;
    }
};

const ACTION_TYPE_OPTIONS: ActionOptions[] = [
    { label: 'AP History', value: HISTORY_TYPE.AP },
    { label: 'PP History', value: HISTORY_TYPE.PP },
    { label: 'API History', value: HISTORY_TYPE.API },
    { label: 'RFU History', value: HISTORY_TYPE.RFU },
];

const StoreHistoryPage = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const [
        {
            tableLoading,
            searchQuery,
            historyList,
            rowCount,
            sortModel,
            paginationModel,
            openSummaryDialog,
            summaryInfo,
            jobId,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);
    const resizeFrameRef = useRef<number | null>(null);
    const historyTableApiRef = useRef({} as GridApi);
    const { storeWallTimeToUtcString, isStoreReady, isTimezoneReady, storeDetailsError, formatUtcToStoreTime } =
        useOutletContext<StoreMgmtOutletContext>();
    const [appliedActionType, setAppliedActionType] = useState<HistoryType>(HISTORY_TYPE.PP);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    // Table Memoized
    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'created_at',
                headerName: t('Date Time'),
                flex: 1,
                minWidth: 140,
                maxWidth: 170,
                sortable: true,
                hideable: false,
            },
            {
                field: 'event_message',
                headerName: t('Event Message'),
                flex: 2.5,
                minWidth: 250,
                sortable: false,
                hideable: false,
                renderCell: (params) => {
                    const { result, action_detail, action_type, job_id } = params.row;
                    const hasPpSummary = action_type === 'pp_history_raw' && job_id;
                    const hasRfuSummary = action_type === 'rfu_history' && result;
                    return (
                        <BaseTableColumn>
                            <Box display="flex" alignItems="center" height="100%">
                                {hasPpSummary && (
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(_event) => {
                                            _event.stopPropagation();
                                            dispatch({ type: 'SET_SUMMARY_DIALOG_OPEN', payload: true });
                                            dispatch({ type: 'SET_JOB_ID', payload: job_id });
                                        }}
                                    >
                                        <Summarize />
                                    </IconButton>
                                )}
                                {hasRfuSummary && (
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(_event) => {
                                            _event.stopPropagation();
                                            dispatch({ type: 'SET_SUMMARY_DIALOG_OPEN', payload: true });
                                            dispatch({ type: 'SET_SUMMARY_INFO', payload: result });
                                        }}
                                    >
                                        <Summarize />
                                    </IconButton>
                                )}
                                {action_detail.default_message || ''}
                            </Box>
                        </BaseTableColumn>
                    );
                },
            },
            {
                field: 'ip_address',
                headerName: t('Initiator'),
                flex: 1,
                minWidth: 140,
                maxWidth: 150,
                renderCell: (params) => {
                    const value = params.row.user_name || params.row.ip_address || '-';
                    return <BaseTableColumn columnValue={value} />;
                },
                sortable: true,
            },
            ...(appliedActionType === HISTORY_TYPE.PP
                ? [
                      {
                          field: 'action',
                          type: 'actions' as const,
                          headerName: t('Download'),
                          flex: 1,
                          maxWidth: 100,
                          sortable: false,
                          getActions: ({ row }: GridRowParams<HistoryRecord>) => [
                              <GridActionsCellItem
                                  icon={<Download />}
                                  label={t('Download')}
                                  sx={{
                                      color: 'primary.main',
                                  }}
                                  onClick={() => {
                                      saveAs(row.action_detail.download_link, row.action_detail.filename);
                                  }}
                              />,
                          ],
                      },
                  ]
                : []),
        ],
        [appliedActionType, t],
    );

    const displayLabel = useMemo(() => {
        const foundOption = ACTION_TYPE_OPTIONS.find((option) => option.value === appliedActionType) as ActionOptions;
        return foundOption.label;
    }, [appliedActionType]);

    // Methods
    const fetchHistoryListHandler = useCallback(async () => {
        if (
            [searchQuery.start_time, searchQuery.end_time].some(
                (value) => value?.isValid() && value.isBefore(MIN_DATE_TIME),
            ) ||
            hasReversedTimeRange(searchQuery)
        )
            return;

        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        const toApiTime = (value: typeof searchQuery.start_time) => {
            const converted = storeWallTimeToUtcString(value);
            return converted == null ? null : String(converted);
        };
        const postData = {
            store_id: manageStoreId as number,
            start_time: toApiTime(searchQuery.start_time),
            end_time: toApiTime(searchQuery.end_time),
            keyword: searchQuery.keyword || '',
            action_type: searchQuery.action_type as string,
            current_page: paginationModel.page + 1,
            data_per_page: paginationModel.pageSize,
            sort_field: sortModel.field,
            sort_order: sortModel.sort,
        };

        try {
            const {
                data: { data, isSuccess, message },
            } = await handleFetchHistoryListAPI(postData, { signal: abortController.signal });
            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
            if (isSuccess) {
                const historyInfo = data.aaData || [];
                const formattedHistoryInfo = historyInfo.map((history: HistoryRecord) => {
                    return {
                        ...history,
                        created_at: formatUtcToStoreTime(history.created_at),
                    };
                });
                setAppliedActionType(postData.action_type as HistoryType);
                dispatch({
                    type: 'SET_HISTORY_TABLE',
                    payload: {
                        ...data,
                        aaData: formattedHistoryInfo,
                    },
                });
            } else {
                setNotification({ message, severity: 'error' });
            }
        } catch {
            if (abortController.signal.aborted) return;
            dispatch({ type: 'SET_TABLE_LOADING', payload: false });
            setNotification({
                message: t('An unexpected error occurred'),
                severity: 'error',
            });
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                fetchAbortControllerRef.current = null;
            }
        }
    }, [
        manageStoreId,
        searchQuery,
        paginationModel,
        sortModel,
        storeWallTimeToUtcString,
        formatUtcToStoreTime,
        setNotification,
        t,
    ]);

    const pageChangeHandler = useCallback(
        (pageModel: GridPaginationModel) => {
            if (paginationModel.pageSize !== pageModel.pageSize) {
                // When the table page size changes, reset the page to 1.
                dispatch({
                    type: 'SET_PAGINATION_MODEL',
                    payload: { ...pageModel, page: 0 },
                });
            } else {
                dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
            }
        },
        [paginationModel],
    );

    const sortModelChangeHandler = useCallback((sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field, sort: sort as 'asc' | 'desc' },
            });
        } else {
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field: null, sort: null },
            });
        }
    }, []);

    const getTodayRange = useCallback(() => {
        const current = dayjs();

        return {
            start_time: current.startOf('day'),
            end_time: current.endOf('day'),
        };
    }, []);

    const clearHistoryHandler = useCallback(() => {
        dispatch({ type: 'CLEAR_FILTER', payload: getTodayRange() });
        setAppliedActionType(HISTORY_TYPE.PP);
    }, [getTodayRange]);

    const keyUpHandler = useCallback(
        (event: KeyboardEvent<Element>) => {
            if (event.key === 'Enter') {
                fetchHistoryListHandler();
            }
        },
        [fetchHistoryListHandler],
    );

    const handleSearchQueryChange = useCallback(
        (field: keyof HistorySearchQuery, value: HistorySearchQuery[keyof HistorySearchQuery]) =>
            dispatch({
                type: 'SET_SEARCH_QUERY',
                payload: {
                    ...searchQuery,
                    [field]: value,
                },
            }),
        [searchQuery],
    );

    const handleColumnResize = useCallback(() => {
        if (resizeFrameRef.current !== null) return;

        resizeFrameRef.current = requestAnimationFrame(() => {
            historyTableApiRef.current.resetRowHeights();
            resizeFrameRef.current = null;
        });
    }, [historyTableApiRef]);

    // Memo
    const BaseTableMemo = useMemo(
        () => (
            <BaseDataTable
                rows={historyList}
                columns={columns}
                apiRef={historyTableApiRef}
                loading={tableLoading}
                getRowId={(row: HistoryRecord) => row.history_id}
                paginationModel={paginationModel}
                paginationMode="server"
                rowCount={rowCount}
                onPaginationModelChange={pageChangeHandler}
                sortingMode="server"
                onSortModelChange={sortModelChangeHandler}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                onColumnResize={handleColumnResize}
            />
        ),
        [
            historyList,
            columns,
            tableLoading,
            paginationModel,
            rowCount,
            pageChangeHandler,
            sortModelChangeHandler,
            columnVisibilityModel,
            historyTableApiRef,
            handleColumnResize,
        ],
    );

    // Hooks
    useEffect(() => {
        dispatch({
            type: 'SET_SEARCH_QUERY',
            payload: {
                ...defaultState.searchQuery,
                ...getTodayRange(),
            },
        });
    }, [getTodayRange]);

    // Hooks
    // isStoreReady (which already factors in manageStoreId) is used instead
    // of manageStoreId directly: on a store switch, the clear-on-store-change
    // effect further down resets searchQuery/paginationModel; that
    // paginationModel change is what re-triggers this effect, so it always
    // runs with the new store's already-cleared filters. isTimezoneReady is
    // additionally required because toApiTime/formatUtcToStoreTime below bake
    // store-timezone-formatted values into the outgoing request and rows.
    useEffect(() => {
        if (storeDetailsError) {
            dispatch({ type: 'SET_TABLE_LOADING', payload: false });
            return;
        }
        if (!isStoreReady || !isTimezoneReady) return;
        fetchHistoryListHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStoreReady, isTimezoneReady, storeDetailsError, paginationModel, sortModel]);

    const isFirstStoreRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstStoreRenderRef.current) {
            isFirstStoreRenderRef.current = false;
            return;
        }
        clearHistoryHandler();
    }, [manageStoreId, clearHistoryHandler]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
            if (resizeFrameRef.current !== null) cancelAnimationFrame(resizeFrameRef.current);
        };
    }, []);

    return (
        <>
            <BasePaper isFullHeight={false}>
                <BaseTitle icon={<WorkHistory />} title={t('Store History')} />
                {manageStoreId ? (
                    <>
                        <StoreFilterBlock handleSearch={fetchHistoryListHandler} handleClear={clearHistoryHandler}>
                            <FormControl fullWidth>
                                <DateTimePicker
                                    label={t('Start Time')}
                                    format="YYYY-MM-DD HH:mm"
                                    ampm={false}
                                    value={searchQuery.start_time}
                                    minDateTime={MIN_DATE_TIME}
                                    maxDateTime={searchQuery.end_time || undefined}
                                    onChange={(value) => handleSearchQueryChange('start_time', value)}
                                    slotProps={{
                                        textField: {
                                            helperText:
                                                searchQuery.start_time?.isValid() &&
                                                searchQuery.start_time.isBefore(MIN_DATE_TIME)
                                                    ? t('Dates must be 1900-01-01 or later.')
                                                    : undefined,
                                        },
                                    }}
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <DateTimePicker
                                    label={t('End Time')}
                                    format="YYYY-MM-DD HH:mm"
                                    ampm={false}
                                    value={searchQuery.end_time}
                                    minDateTime={
                                        searchQuery.start_time?.isAfter(MIN_DATE_TIME)
                                            ? searchQuery.start_time
                                            : MIN_DATE_TIME
                                    }
                                    onChange={(value) => handleSearchQueryChange('end_time', value)}
                                    slotProps={{
                                        textField: {
                                            helperText:
                                                searchQuery.end_time?.isValid() &&
                                                searchQuery.end_time.isBefore(MIN_DATE_TIME)
                                                    ? t('Dates must be 1900-01-01 or later.')
                                                    : hasReversedTimeRange(searchQuery)
                                                      ? t('End time cannot be before start time.')
                                                      : undefined,
                                        },
                                    }}
                                />
                            </FormControl>
                            <BaseTextField
                                label={t('Keyword')}
                                placeholder={t('Keyword')}
                                value={searchQuery.keyword}
                                onChange={(_event) => handleSearchQueryChange('keyword', _event.target.value)}
                                onKeyUp={keyUpHandler}
                            />

                            <FormControl fullWidth>
                                <BaseSelect
                                    label={t('Action Type')}
                                    options={ACTION_TYPE_OPTIONS}
                                    value={searchQuery.action_type}
                                    onChange={(_event) =>
                                        handleSearchQueryChange('action_type', _event.target.value as string)
                                    }
                                />
                            </FormControl>
                        </StoreFilterBlock>

                        <BaseCard>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle1">
                                    {t('Action Type : {{label}}', {
                                        label: displayLabel,
                                    })}
                                </Typography>
                            </Box>
                            {BaseTableMemo}
                        </BaseCard>
                        <SummaryDialog
                            open={openSummaryDialog}
                            summaryInfo={summaryInfo}
                            jobId={jobId}
                            type={searchQuery.action_type as HistoryType}
                            onClose={() => dispatch({ type: 'SET_SUMMARY_DIALOG_OPEN', payload: false })}
                            onExited={() => {
                                dispatch({ type: 'SET_SUMMARY_INFO', payload: {} as SummaryInfo });
                                dispatch({ type: 'SET_JOB_ID', payload: '' });
                            }}
                        />
                    </>
                ) : (
                    <NoStoreSelectionBlock />
                )}
            </BasePaper>
        </>
    );
};

export default StoreHistoryPage;
